/**
 * MyBusiness — the full business-snapshot page behind the home band (Ship 4
 * of the My Business dashboard plan, Aug 24 2026).
 *
 * Tabs (URL-backed so back-from-deal preserves the tab):
 *   • Now — money outlook, projected signed-unbuilt book, funnel vs goals,
 *     deals going cold.
 *   • Past — monthly earnings history, SPIFF history, and the "Closed &
 *     built" list (the closed-deals-vanish fix: PipelineView hides closing
 *     stages from field roles; this is where a rep looks backward).
 * Lenses (time↔money) and the TL/ASM Team tab ship next.
 *
 * All money comes from useBusinessSnapshot → groupByPerson — identical math
 * to My Pipeline → Commissions and the finance board. Estimates are always
 * labeled PROJECTED and never mixed into earned totals.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTabParam } from "../lib/useTabParam";
import { fmtUSD } from "../lib/analytics";
import { logCardClick } from "../lib/uiEvents";
import { FIELD_ROLES, LEAD_SOURCE_LABELS, etDay, todayET } from "../lib/businessSnapshot";
import { useBusinessSnapshot } from "../components/home/useBusinessSnapshot";
import DealLink from "../components/DealLink";
import DealAgeChips from "../components/shared/DealAgeChips";

type Tab = "now" | "past" | "lenses" | "team";
const TABS: Array<{ key: Tab; label: string }> = [
  { key: "now", label: "Now" },
  { key: "past", label: "Past" },
  { key: "lenses", label: "Time ↔ Money" },
  { key: "team", label: "Team" }, // TL/ASM only — filtered below
];

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m: string) => {
  const [y, mo] = m.split("-").map(Number);
  return `${MONTH_NAMES[mo - 1]} ${y}`;
};
const fmtHalf = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

function Card({ title, sub, right, children }: { title: string; sub?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-4 overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-2 border-b border-slate-100">
        <div>
          <div className="text-sm font-semibold text-bardin-ink">{title}</div>
          {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
        </div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

const Empty = ({ children }: { children: React.ReactNode }) => (
  <div className="px-4 py-6 text-center text-sm text-slate-400">{children}</div>
);

export default function MyBusiness() {
  const { member, role, isSuperUser } = useAuth();
  const [tab, setTab] = useTabParam<Tab>("tab", "now", ["now", "past", "lenses", "team"]);
  const { core, error, refresh, lenses, lensesLoading, loadLenses } = useBusinessSnapshot(member, role, !!isSuperUser);
  const isLead = role === "team_lead" || role === "asm";
  const visibleTabs = TABS.filter((t) => t.key !== "team" || isLead);
  const effectiveTab: Tab = tab === "team" && !isLead ? "now" : tab;

  // Lenses load lazily — only when the tab is first opened and phase 1 is in.
  useEffect(() => {
    if (tab === "lenses" && core) void loadLenses();
  }, [tab, core, loadLenses]);

  if (!member) return null;
  const isFieldMoney = !!role && FIELD_ROLES.has(role);
  const firstName = member.full_name?.split(" ")[0] ?? "My";

  const nowMonth = todayET().slice(0, 7);
  const thisMonthPaid = core?.earnings.rows.find((r) => r.month === nowMonth)?.paid ?? 0;
  const maxMonthPaid = Math.max(...(core?.earnings.rows.map((r) => r.paid) ?? [0]), 1);
  const ytd = core?.earnings.rows.filter((r) => r.month.startsWith(nowMonth.slice(0, 4)))
    .reduce((s, r) => s + r.paid, 0) ?? 0;
  const spiffYtd = core?.spiffs.reduce((s, r) => s + r.amount, 0) ?? 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h1 className="text-2xl font-semibold text-bardin-ink">{firstName}'s Business</h1>
          <p className="text-sm text-slate-500">Past, present, and what's coming — same math as the commission board.</p>
          <p className="text-xs text-slate-500">Every pay day, statement and your goal: <Link to="/my-pay" className="font-semibold text-bardin-teal underline">My Pay</Link></p>
        </div>
        <button onClick={refresh} className="text-[11px] font-bold text-slate-500 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">↻ Refresh</button>
      </div>

      <div className="border-b border-slate-200 mb-4">
        <nav className="flex gap-1">
          {visibleTabs.map((t) => (
            <button key={t.key} onClick={() => { logCardClick(`mybiz_page_tab_${t.key}`); setTab(t.key); }}
              className={"px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 " +
                (tab === t.key ? "text-bardin-teal border-bardin-teal" : "text-slate-500 border-transparent hover:text-slate-700")}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {error && <div className="p-4 bg-red-50 text-red-700 rounded-xl mb-4 text-sm">Couldn't load your snapshot — {error}</div>}
      {!core && !error && <div className="p-8 text-slate-500 text-sm">Loading your business…</div>}

      {core && effectiveTab === "now" && (
        <>
          {/* Money outlook */}
          {isFieldMoney && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Paid this month</div>
                <div className="text-xl font-extrabold text-emerald-600">{fmtUSD(thisMonthPaid)}</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Pending approval</div>
                <div className="text-xl font-extrabold text-bardin-ink">{fmtUSD(core.outlook.pendingQueue)}</div>
                <div className="text-[10px] text-slate-400">{core.outlook.pendingDeals} deal{core.outlook.pendingDeals === 1 ? "" : "s"} · Chad's queue</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Approved · awaiting pay</div>
                <div className="text-xl font-extrabold text-bardin-teal">{fmtUSD(core.outlook.approvedAwaitingPay)}</div>
                <div className="text-[10px] text-slate-400">{core.outlook.approvedDeals} deal{core.outlook.approvedDeals === 1 ? "" : "s"}</div>
              </div>
              <div className="bg-white rounded-xl border border-amber-200 bg-amber-50/40 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wide text-amber-600">Projected 🔮</div>
                <div className="text-xl font-extrabold text-amber-700">{fmtUSD(core.outlook.projectedSignedUnbuilt)}</div>
                <div className="text-[10px] text-amber-600">signed, not built — estimate</div>
              </div>
            </div>
          )}

          {/* Held commissions */}
          {isFieldMoney && core.held.length > 0 && (
            <Card title="🔒 Held commissions" sub="Blocked until the condition clears — talk to Chad/Shannon">
              <ul className="divide-y divide-slate-100">
                {core.held.map((h) => (
                  <li key={h.deal.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-bardin-ink truncate"><DealLink id={h.deal.id}>{h.deal.homeowner_name ?? "(no name)"}</DealLink></div>
                      <div className="text-[11px] text-rose-600">{h.reason}</div>
                    </div>
                    <span className="text-sm font-semibold text-bardin-ink shrink-0">{fmtUSD(h.myPendingAmount)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Signed-unbuilt book with projections */}
          {isFieldMoney && (
            <Card
              title="Coming up — signed, not yet built"
              sub={`${core.backlog.count} job${core.backlog.count === 1 ? "" : "s"} · ${core.backlog.scheduled} scheduled (${core.backlog.next14} in the next 14d) · ${core.backlog.unscheduled} unscheduled${core.backlog.stale30 ? ` · ${core.backlog.stale30} waiting 30d+` : ""}`}
              right={<span className="text-[9px] font-extrabold uppercase tracking-wide rounded px-1.5 py-0.5 bg-amber-100 text-amber-700">Projected</span>}
            >
              {core.outlook.projectedDeals.length === 0 ? (
                <Empty>Nothing signed and waiting — go get some ink. ✍️</Empty>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {core.outlook.projectedDeals.map(({ deal, myEstimate }) => (
                    <li key={deal.id} className="px-4 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm text-bardin-ink truncate"><DealLink id={deal.id}>{deal.homeowner_name ?? "(no name)"}</DealLink></div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {deal.property_street ?? "—"}
                            {deal.build_scheduled_date ? ` · 🔨 ${deal.build_scheduled_date}` : " · not scheduled"}
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-amber-700 shrink-0">~{fmtUSD(myEstimate)}</span>
                      </div>
                      <DealAgeChips deal={deal} className="mt-1" />
                    </li>
                  ))}
                </ul>
              )}
              <div className="px-4 py-2 text-[10px] text-amber-600 bg-amber-50/60 border-t border-amber-100">
                🔮 Estimates move until the final invoice — scope changes, credits, and reviews all shift the number.
              </div>
            </Card>
          )}

          {/* Funnel vs goals */}
          <Card title="This week's funnel" sub="Appointments → claims → signed IAs → builds, vs your weekly goal">
            <div className="grid grid-cols-4 gap-2 px-4 py-4">
              {([
                ["Apps", core.funnel.apps, core.goals?.apps],
                ["Claims", core.funnel.claims, core.goals?.claims],
                ["Signed IA", core.funnel.signedIA, core.goals?.builds_scheduled],
                ["Builds", core.funnel.builds, core.goals?.builds],
              ] as Array<[string, number, number | undefined]>).map(([label, val, goal]) => {
                const hit = goal != null && goal > 0 && val >= goal;
                const pct = goal && goal > 0 ? Math.min(100, (val / goal) * 100) : null;
                return (
                  <div key={label} className="text-center">
                    <div className={"text-2xl font-extrabold " + (hit ? "text-emerald-600" : "text-bardin-ink")}>
                      {fmtHalf(val)}{goal != null && goal > 0 && <span className="text-xs font-bold text-slate-400">/{fmtHalf(goal)}</span>}
                    </div>
                    <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mt-0.5">{label}</div>
                    {pct != null && (
                      <div className="h-1 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                        <div className={"h-full rounded-full " + (hit ? "bg-emerald-500" : "bg-bardin-teal")} style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="px-4 pb-3 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
              {core.funnel.claimPerApp != null && <span>Claims per app: <b>{(core.funnel.claimPerApp * 100).toFixed(0)}%</b></span>}
              {core.funnel.signPerClaim != null && <span>Signed per claim: <b>{(core.funnel.signPerClaim * 100).toFixed(0)}%</b></span>}
            </div>
          </Card>

          {/* Going cold */}
          <Card title="🥶 Going cold" sub="Your live deals sitting 14+ days in the same stage">
            {core.cold.length === 0 ? (
              <Empty>Nothing going cold — your book is moving.</Empty>
            ) : (
              <ul className="divide-y divide-slate-100">
                {core.cold.slice(0, 10).map(({ deal, days }) => (
                  <li key={deal.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-bardin-ink truncate"><DealLink id={deal.id}>{deal.homeowner_name ?? "(no name)"}</DealLink></div>
                      <div className="text-[11px] text-slate-400 truncate">{deal.property_street ?? "—"} · {String(deal.stage ?? "").replace(/_/g, " ")}</div>
                    </div>
                    <span className={"text-xs font-bold shrink-0 " + (days >= 30 ? "text-rose-600" : "text-amber-600")}>{days}d</span>
                  </li>
                ))}
                {core.cold.length > 10 && <li className="px-4 py-2 text-[11px] text-slate-400">+{core.cold.length - 10} more in your pipeline</li>}
              </ul>
            )}
          </Card>
        </>
      )}

      {core && effectiveTab === "past" && (
        <>
          {/* Earnings history */}
          {isFieldMoney && (
            <Card
              title="Earnings history"
              sub="Commission paid to you, by payout month — reconciles with the commission board. Each pay day's statement is on My Pay."
              right={
                <div className="text-right">
                  <div className="text-base font-extrabold text-bardin-ink">{fmtUSD(core.earnings.totalPaid)}</div>
                  <div className="text-[10px] text-slate-400">all-time paid</div>
                </div>
              }
            >
              <ul className="divide-y divide-slate-50 px-4 py-2">
                {[...core.earnings.rows].reverse().map((r) => (
                  <li key={r.month} className="py-1.5 flex items-center gap-3">
                    <span className="text-[11px] font-semibold text-slate-500 w-16 shrink-0">{monthLabel(r.month)}</span>
                    <div className="flex-1 h-3 rounded bg-slate-100 overflow-hidden">
                      <div className="h-full rounded bg-emerald-500" style={{ width: `${(r.paid / maxMonthPaid) * 100}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-bardin-ink w-24 text-right shrink-0">{r.paid > 0 ? fmtUSD(r.paid) : "—"}</span>
                  </li>
                ))}
              </ul>
              <div className="px-4 py-2.5 border-t border-slate-100 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-slate-500">
                <span>YTD: <b className="text-bardin-ink">{fmtUSD(ytd)}</b></span>
                <span>Deals paid: <b className="text-bardin-ink">{core.earnings.paidDealCount}</b></span>
                {core.earnings.avgPerPaidDeal != null && <span>Avg per deal: <b className="text-bardin-ink">{fmtUSD(core.earnings.avgPerPaidDeal)}</b></span>}
                {core.earnings.totalApprovedAwaiting > 0 && <span>Approved awaiting payout: <b className="text-bardin-teal">{fmtUSD(core.earnings.totalApprovedAwaiting)}</b></span>}
              </div>
            </Card>
          )}

          {/* SPIFFs */}
          <Card title="🏆 SPIFFs won" sub="This year, from the weekly program + manual awards"
            right={<div className="text-base font-extrabold text-bardin-ink">{fmtUSD(spiffYtd)}</div>}>
            {core.spiffs.length === 0 ? (
              <Empty>No SPIFFs yet this year — Monday Madness is one claim away.</Empty>
            ) : (
              <ul className="divide-y divide-slate-50">
                {core.spiffs.slice(0, 8).map((s, i) => (
                  <li key={i} className="px-4 py-2 flex items-center justify-between gap-2 text-sm">
                    <span className="text-bardin-ink truncate">{s.name}</span>
                    <span className="shrink-0 flex items-center gap-2">
                      <span className="text-[10px] text-slate-400">{etDay(s.claimed_at)}</span>
                      <span className="font-semibold">{fmtUSD(s.amount)}</span>
                      {s.paid ? <span className="text-[9px] font-bold text-emerald-600">PAID</span> : <span className="text-[9px] font-bold text-amber-600">DUE</span>}
                    </span>
                  </li>
                ))}
                {core.spiffs.length > 8 && <li className="px-4 py-2 text-[11px] text-slate-400">+{core.spiffs.length - 8} more</li>}
              </ul>
            )}
          </Card>

          {/* Closed & built */}
          {isFieldMoney && (
            <Card title="Closed & built" sub="Every deal you've been paid on, newest first — your track record">
              {core.closedByMonth.length === 0 ? (
                <Empty>No paid deals yet — they'll build up here.</Empty>
              ) : (
                core.closedByMonth.map((g) => (
                  <details key={g.month} className="border-b border-slate-100 last:border-b-0" open={g.month === nowMonth}>
                    <summary className="cursor-pointer select-none px-4 py-2.5 flex items-center justify-between text-sm">
                      <span className="font-semibold text-bardin-ink">{monthLabel(g.month)} <span className="text-slate-400 font-normal">· {g.deals.length} deal{g.deals.length === 1 ? "" : "s"}</span></span>
                      <span className="font-bold text-emerald-600">{fmtUSD(g.total)}</span>
                    </summary>
                    <ul className="divide-y divide-slate-50 border-t border-slate-100">
                      {g.deals.map(({ deal, myTotal }) => (
                        <li key={deal.id} className="px-4 py-2 pl-6 flex items-center justify-between gap-2 text-sm">
                          <div className="min-w-0">
                            <div className="text-bardin-ink truncate"><DealLink id={deal.id}>{deal.homeowner_name ?? "(no name)"}</DealLink></div>
                            <div className="text-[11px] text-slate-400 truncate">{deal.property_street ?? "—"}</div>
                          </div>
                          <span className="font-semibold shrink-0">{fmtUSD(myTotal)}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))
              )}
            </Card>
          )}

          <Link to="/my-pipeline?tab=commissions" onClick={() => logCardClick("mybiz_page_commissions_link")}
            className="block text-center text-sm font-bold text-bardin-teal py-2">
            Full commission line-item detail ›
          </Link>
        </>
      )}

      {core && effectiveTab === "lenses" && (
        <>
          {lensesLoading && !lenses && <div className="p-8 text-slate-500 text-sm">Crunching where your time and money go…</div>}
          {lenses && (
            <>
              {/* Geography */}
              <Card title="🗺 Where you work vs where you earn"
                sub="Knocks/appointments per city (last 90 days) against your commission by deal city (all-time paid + projected)">
                {lenses.geo.length === 0 ? (
                  <Empty>No city data yet — knock some doors and close some deals.</Empty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                          <th className="text-left px-4 py-2">City</th>
                          <th className="text-right px-2 py-2">Knocks</th>
                          <th className="text-right px-2 py-2">Appts</th>
                          <th className="text-right px-2 py-2">Signed</th>
                          <th className="text-right px-4 py-2">My $</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lenses.geo.slice(0, 12).map((r) => {
                          const money = r.myPaid + r.myProjected;
                          const mismatch = (r.knocks >= 25 && money === 0) || (money > 2000 && r.knocks === 0);
                          return (
                            <tr key={r.city} className={"border-b border-slate-50 " + (mismatch ? "bg-amber-50/60" : "")}>
                              <td className="px-4 py-2 font-medium text-bardin-ink">
                                {r.city}
                                {mismatch && <span className="ml-1.5 text-[9px] font-bold text-amber-600" title={r.knocks > 0 ? "Lots of knocking, no money yet" : "Money here but you're not knocking it"}>⚠</span>}
                              </td>
                              <td className="px-2 py-2 text-right text-slate-600">{r.knocks || "—"}</td>
                              <td className="px-2 py-2 text-right text-slate-600">{r.appts || "—"}</td>
                              <td className="px-2 py-2 text-right text-slate-600">{r.dealsSigned || "—"}</td>
                              <td className="px-4 py-2 text-right font-semibold">
                                {money > 0 ? fmtUSD(money) : "—"}
                                {r.myProjected > 0 && <span className="text-[9px] text-amber-600 ml-1">~{fmtUSD(r.myProjected)} proj</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Lead source */}
              <Card title="🎯 What your leads are worth, by source"
                sub="Your whole book (live + closed + lost), classified by where each deal came from">
                {lenses.sources.length === 0 ? (
                  <Empty>No deals to classify yet.</Empty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                          <th className="text-left px-4 py-2">Source</th>
                          <th className="text-right px-2 py-2">Deals</th>
                          <th className="text-right px-2 py-2">Sign %</th>
                          <th className="text-right px-2 py-2">Paid to you</th>
                          <th className="text-right px-4 py-2">Avg/deal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lenses.sources.map((r) => (
                          <tr key={r.bucket} className="border-b border-slate-50">
                            <td className="px-4 py-2 font-medium text-bardin-ink">{LEAD_SOURCE_LABELS[r.bucket]}</td>
                            <td className="px-2 py-2 text-right text-slate-600">{r.deals}</td>
                            <td className="px-2 py-2 text-right text-slate-600">{r.signRate != null ? `${Math.round(r.signRate * 100)}%` : "—"}</td>
                            <td className="px-2 py-2 text-right font-semibold">{r.myPaid > 0 ? fmtUSD(r.myPaid) : "—"}</td>
                            <td className="px-4 py-2 text-right text-slate-600">{r.avgPerPaidDeal != null ? fmtUSD(r.avgPerPaidDeal) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Stage flow */}
              <Card title="⏳ Where your deals spend time"
                sub="Median days your deals sit in each stage before moving — and where the lost ones died">
                {lenses.stages.length === 0 ? (
                  <Empty>No stage history yet.</Empty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                          <th className="text-left px-4 py-2">Stage</th>
                          <th className="text-right px-2 py-2">Median days</th>
                          <th className="text-right px-2 py-2">There now</th>
                          <th className="text-right px-4 py-2">Died here</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lenses.stages.slice(0, 12).map((r) => (
                          <tr key={r.stage} className={"border-b border-slate-50 " + (r.diedHere >= 3 ? "bg-rose-50/50" : "")}>
                            <td className="px-4 py-2 font-medium text-bardin-ink">{r.stage.replace(/_/g, " ")}</td>
                            <td className="px-2 py-2 text-right text-slate-600">{r.medianDays != null ? `${r.medianDays}d` : "—"}</td>
                            <td className="px-2 py-2 text-right text-slate-600">{r.nowSitting || "—"}</td>
                            <td className="px-4 py-2 text-right font-semibold text-rose-600">{r.diedHere || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Claim outcomes */}
              <Card title="📋 Your claim outcomes"
                sub={`Approval rate on claims you've filed${lenses.claims.agg.dec < 8 ? " — small sample, read directionally" : ""}`}>
                <div className="grid grid-cols-4 gap-2 px-4 py-4 text-center">
                  <div>
                    <div className="text-2xl font-extrabold text-bardin-ink">{lenses.claims.agg.dec > 0 ? `${Math.round((lenses.claims.agg.ap / lenses.claims.agg.dec) * 100)}%` : "—"}</div>
                    <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Approved</div>
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold text-emerald-600">{lenses.claims.agg.ap}</div>
                    <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Won</div>
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold text-rose-600">{lenses.claims.agg.de}</div>
                    <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Denied</div>
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold text-slate-400">{lenses.claims.agg.op}</div>
                    <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Open</div>
                  </div>
                </div>
                {lenses.claims.byCarrier.length > 0 && (
                  <ul className="divide-y divide-slate-50 border-t border-slate-100">
                    {lenses.claims.byCarrier.slice(0, 6).map((c) => (
                      <li key={c.carrier} className="px-4 py-2 flex items-center justify-between text-sm">
                        <span className="text-bardin-ink capitalize truncate">{c.carrier}</span>
                        <span className="text-slate-600 shrink-0">
                          {c.agg.dec > 0 ? `${Math.round((c.agg.ap / c.agg.dec) * 100)}% of ${c.agg.dec}` : `${c.agg.c} filed`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              {/* Activity mix */}
              <Card title="⚡ Your last 30 days of effort" sub="Field time + comms next to what it produced">
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 px-4 py-4 text-center">
                  {([
                    ["Field hrs", lenses.activity.fieldHours],
                    ["Knocks", lenses.activity.knocks],
                    ["Contacts", lenses.activity.contacts],
                    ["Appts set", lenses.activity.appts],
                    ["Calls", lenses.activity.callsOnMyDeals],
                    ["Texts sent", lenses.activity.textsISent],
                  ] as Array<[string, number]>).map(([label, v]) => (
                    <div key={label}>
                      <div className="text-xl font-extrabold text-bardin-ink">{v}</div>
                      <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">{label}</div>
                    </div>
                  ))}
                </div>
                {lenses.activity.apptsPerFieldHour != null && (
                  <div className="px-4 pb-3 text-[11px] text-slate-500">
                    Appointments per field hour: <b className="text-bardin-ink">{lenses.activity.apptsPerFieldHour}</b>
                    {lenses.activity.miles > 0 && <> · {lenses.activity.miles} mi walked</>}
                  </div>
                )}
              </Card>
            </>
          )}
          {!lensesLoading && !lenses && <Empty>Couldn't load the lenses — try Refresh.</Empty>}
        </>
      )}

      {core && effectiveTab === "team" && isLead && (
        <>
          <Card title="👥 Your team this week"
            sub={`Production per member (same numbers as the crew board) + your ${role === "asm" ? "4% ASM" : "1% TL"} override on their deals`}>
            {!core.team || core.team.length === 0 ? (
              <Empty>No team members roll up to you yet.</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                      <th className="text-left px-4 py-2">Rep</th>
                      <th className="text-right px-2 py-2">Apps</th>
                      <th className="text-right px-2 py-2">Claims</th>
                      <th className="text-right px-2 py-2">Signed</th>
                      <th className="text-right px-2 py-2">Builds</th>
                      <th className="text-right px-2 py-2">Your override</th>
                      <th className="text-right px-4 py-2">Projected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {core.team.map((r) => (
                      <tr key={r.id} className="border-b border-slate-50">
                        <td className="px-4 py-2 font-medium text-bardin-ink">
                          {r.name}
                          {r.role === "team_lead" && <span className="ml-1.5 text-[9px] font-bold text-amber-700 bg-amber-50 px-1 py-0.5 rounded">TL</span>}
                        </td>
                        <td className="px-2 py-2 text-right text-slate-600">{fmtHalf(r.apps)}</td>
                        <td className="px-2 py-2 text-right text-slate-600">{fmtHalf(r.claims)}</td>
                        <td className="px-2 py-2 text-right text-slate-600">{fmtHalf(r.signedIA)}</td>
                        <td className="px-2 py-2 text-right text-slate-600">{fmtHalf(r.builds)}</td>
                        <td className="px-2 py-2 text-right font-semibold">
                          {r.myOverridePaid + r.myOverridePending > 0 ? fmtUSD(r.myOverridePaid + r.myOverridePending) : "—"}
                        </td>
                        <td className="px-4 py-2 text-right text-amber-700">{r.myOverrideProjected > 0 ? `~${fmtUSD(r.myOverrideProjected)}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-4 py-2 text-[10px] text-slate-400 border-t border-slate-100">
              Production is the public crew-board data. Override figures are yours alone — nobody's personal commission is shown.
            </div>
          </Card>

          {core.teamBacklog && (
            <Card title="Team backlog — signed, not built"
              sub="Across you + your team">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-4 py-4 text-center">
                <div>
                  <div className="text-2xl font-extrabold text-bardin-ink">{core.teamBacklog.count}</div>
                  <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Jobs waiting</div>
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-bardin-teal">{fmtUSD(core.teamBacklog.total)}</div>
                  <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Contracted</div>
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-bardin-orange">{core.teamBacklog.next14}</div>
                  <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Building in 14d</div>
                </div>
                <div>
                  <div className={"text-2xl font-extrabold " + (core.teamBacklog.stale30 > 0 ? "text-rose-600" : "text-bardin-ink")}>{core.teamBacklog.stale30}</div>
                  <div className="text-[10px] text-slate-500 font-semibold uppercase mt-0.5">Waiting 30d+</div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
